import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { generateClient } from 'aws-amplify/api';
import { LIST_MY_NOTIFICATIONS, ListMyNotificationsResponse, AppNotification } from '../graphql/notifications';
import { NotificationsPanel } from './NotificationsPanel';

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
    }
  }
`;

const TOAST_DURATION = 5000; // 5 seconds

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [realtimeNotifications, setRealtimeNotifications] = useState<AppNotification[]>([]);
  const [toastNotification, setToastNotification] = useState<AppNotification | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const { data: userData } = useQuery<{ getCurrentUser: { id: string } }>(GET_CURRENT_USER);
  const userId = userData?.getCurrentUser?.id;
  
  const { data, refetch } = useQuery<ListMyNotificationsResponse>(LIST_MY_NOTIFICATIONS, {
    variables: { limit: 50 },
    fetchPolicy: 'cache-and-network',
    skip: !userId,
  });

  // Combine server data with realtime notifications
  const serverNotifications = data?.listMyNotifications?.items || [];
  const serverUnreadCount = data?.listMyNotifications?.unreadCount || 0;
  
  // Merge realtime notifications with server data (realtime first, deduplicated)
  const allNotifications = [...realtimeNotifications];
  serverNotifications.forEach(n => {
    if (!allNotifications.some(existing => existing.id === n.id)) {
      allNotifications.push(n);
    }
  });
  
  // Count unread from realtime + server unread count (avoiding double counting)
  const realtimeUnreadCount = realtimeNotifications.filter(n => !n.isRead).length;
  const unreadCount = serverUnreadCount + realtimeUnreadCount;

  // Show toast notification
  const showToast = useCallback((notification: AppNotification) => {
    // Clear any existing timeout
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    
    setToastNotification(notification);
    
    // Auto-hide after duration
    toastTimeoutRef.current = setTimeout(() => {
      setToastNotification(null);
    }, TOAST_DURATION);
  }, []);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  // Clear realtime notifications when we refetch (they'll be in server data)
  const handleRefetch = useCallback(async () => {
    const result = await refetch();
    if (result.data?.listMyNotifications) {
      setRealtimeNotifications([]);
    }
    return result;
  }, [refetch]);

  // Subscribe to real-time notifications
  useEffect(() => {
    if (!userId) return;

    const client = generateClient();
    
    const subscription = client.graphql({
      query: `
        subscription OnNotificationReceived($userId: ID!) {
          onNotificationReceived(userId: $userId) {
            id
            userId
            type
            title
            body
            data
            isRead
            createdAt
            readAt
          }
        }
      `,
      variables: { userId },
    });

    let sub: any;
    if ('subscribe' in subscription) {
      sub = (subscription as any).subscribe({
        next: (response: any) => {
          const subData = response?.data;
          if (subData?.onNotificationReceived) {
            const newNotification = subData.onNotificationReceived as AppNotification;
            setRealtimeNotifications(prev => {
              const exists = prev.some(n => n.id === newNotification.id);
              if (exists) return prev;
              return [newNotification, ...prev];
            });
            // Show toast for new notification (only if panel is closed)
            if (!isOpen) {
              showToast(newNotification);
            }
          }
        },
        error: (error: any) => {
          console.error('Notification subscription error:', error);
        },
      });
    }

    return () => {
      if (sub?.unsubscribe) {
        sub.unsubscribe();
      }
    };
  }, [userId, isOpen, showToast]);

  // Close panel when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen]);

  // Refetch when panel opens and hide toast
  useEffect(() => {
    if (isOpen) {
      handleRefetch();
      setToastNotification(null);
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    }
  }, [isOpen, handleRefetch]);

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        title={unreadCount > 0 ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}` : 'Notifications'}
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 ? (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>

      {/* Toast notification preview */}
      {toastNotification && !isOpen && (
        <div 
          className="absolute top-full right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 p-3 animate-in slide-in-from-top-2 fade-in duration-200 cursor-pointer z-50"
          onClick={() => {
            setToastNotification(null);
            setIsOpen(true);
          }}
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {toastNotification.title}
              </p>
              <p className="text-xs text-gray-500 truncate mt-0.5">
                {toastNotification.body}
              </p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setToastNotification(null);
              }}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Progress bar */}
          <div className="mt-2 h-0.5 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-red-500 rounded-full"
              style={{
                animation: `shrink ${TOAST_DURATION}ms linear forwards`,
              }}
            />
          </div>
          <style>{`
            @keyframes shrink {
              from { width: 100%; }
              to { width: 0%; }
            }
          `}</style>
        </div>
      )}

      {isOpen && (
        <NotificationsPanel
          notifications={allNotifications}
          onClose={() => setIsOpen(false)}
          refetch={handleRefetch}
        />
      )}
    </div>
  );
}
